import { Link } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { ArrowLeft, Bluetooth, Check, CloudOff, CloudUpload, Loader2, Play, Square, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { useHub } from "@/hub/HubContext";
import { HubStatusBadge } from "./HubStatusBadge";

const SAVE_LABEL = {
  saved: { text: "Saved", icon: Check },
  saving: { text: "Saving…", icon: Loader2, spin: true },
  error: { text: "Couldn't save. Try again", icon: CloudOff },
};
const GUEST_SAVE = { text: "Saved in this browser only", icon: Check };

const ProjectName = ({ name, onNameChange, onNameCommit, hasSetup, saveState, guest }) => {
  const s = guest ? GUEST_SAVE : SAVE_LABEL[saveState] || SAVE_LABEL.saved;
  const SaveIcon = s.icon;
  if (!hasSetup) return <p className="px-2 font-display text-lg font-bold" data-testid="project-name-placeholder">New robot</p>;
  return (
    <>
      <Input
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        onBlur={() => onNameCommit()}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        maxLength={80}
        aria-label="Project name"
        className="h-9 max-w-xs border-transparent bg-transparent px-2 font-display text-lg font-bold shadow-none hover:border-border focus-visible:border-border"
        data-testid="project-name-input"
      />
      <p className={`flex items-center gap-1 px-2 text-[11px] ${saveState === "error" ? "text-destructive" : "text-muted-foreground"}`} data-testid="save-status">
        <SaveIcon className={`h-3 w-3 ${s.spin ? "animate-spin" : ""}`} />{s.text}
      </p>
    </>
  );
};

export const ProjectTopBar = ({ onConnect, onDrive, onEdit, busy, guest = false, ...nameProps }) => {
  const hub = useHub();
  const connected = hub.status === "connected";

  return (
    <div className="sticky top-0 z-30 flex h-auto flex-wrap items-center gap-2 border-b bg-background/90 px-3 py-2 backdrop-blur-xl lg:h-16 lg:flex-nowrap lg:px-4">
      <Button asChild variant="ghost" size="icon" className="rounded-full">
        <Link to={guest ? "/" : "/projects"} data-testid="project-back-button" aria-label={guest ? "Back to home" : "Back to my projects"}><ArrowLeft className="h-5 w-5" /></Link>
      </Button>
      <div className="min-w-0 flex-1 basis-[calc(100%-3rem)] sm:basis-auto">
        <ProjectName guest={guest} {...nameProps} />
      </div>
      <HubStatusBadge status={hub.status} name={hub.name} attempt={hub.reconnectAttempt} />
      {!connected && (
        <Button variant="outline" className="rounded-full" onClick={onConnect} disabled={!hub.supported || hub.status !== "disconnected"} data-testid="hub-connect-button">
          <Bluetooth className="mr-1.5 h-4 w-4" />Connect hub
        </Button>
      )}
      <Button
        onClick={onDrive}
        disabled={!connected || busy || !nameProps.hasSetup}
        title="Send this setup to the hub and start it"
        className="rounded-full bg-accent px-5 text-accent-foreground hover:bg-accent/90"
        data-testid="drive-button"
      >
        {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4 fill-current" />}Drive
      </Button>
      <Button variant="outline" size="icon" className="rounded-full" onClick={hub.stop} disabled={!connected || !hub.running} data-testid="stop-button" aria-label="Stop the robot">
        <Square className="h-4 w-4 fill-current" />
      </Button>
      <ThemeToggle />
      {nameProps.hasSetup && (
        <Button variant="outline" className="rounded-full border-primary/40" onClick={onEdit} title="Edit setup" data-testid="topbar-edit-setup-button">
          <Wand2 className="h-4 w-4 text-primary md:mr-1.5" /><span className="hidden md:inline">Edit setup</span>
        </Button>
      )}
      {guest ? (
        <Button asChild className="rounded-full">
          <Link to="/sign-up" data-testid="guest-save-online-button"><CloudUpload className="mr-1.5 h-4 w-4" />Save online</Link>
        </Button>
      ) : (
        <div data-testid="project-user-menu" className="flex items-center"><UserButton /></div>
      )}
    </div>
  );
};
